import { createPortal } from 'react-dom'

import { useDocument } from '../hooks/useDocument.js'
import { useUniqueId } from '../utils/unique-id.js'

export interface PortalProps {
  children?: React.ReactNode
  idPrefix?: string
}

// keep headless-ui and react-ui-kit portals in same root
const PORTAL_ROOT = 'headlessui-portal-root'

/**
 * Portal is used to transport any component or element to the end of document.body and renders a React tree into it.
 *
 * Useful for rendering a natural React element hierarchy with a different DOM hierarchy
 * to prevent parent styles from clipping or hiding content (for popovers, dropdowns, and modals).
 *
 */
export const Portal = ({ children, idPrefix = '' }: PortalProps) => {
  const { document } = useDocument()

  const uniqueId = useUniqueId(idPrefix || 'portal')

  if (!document?.body) {
    return null
  }

  return createPortal(
    <div data-portal-id={uniqueId} className="isolate">
      {children}
    </div>,
    getPortalRootElement(document) ?? document.body,
  )
}

const getPortalRootElement = (document: Document) => {
  const root = document.getElementById(PORTAL_ROOT)
  if (root) {
    return root
  }

  const newRoot = document.createElement('div')
  newRoot.setAttribute('id', PORTAL_ROOT)
  document.body.appendChild(newRoot)
  return newRoot
}
